import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ticket } from '@vd/types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bug,
  Check,
  CircleAlert,
  Clock3,
  FileCheck2,
  Image as ImageIcon,
  Lightbulb,
  LoaderCircle,
  PlayCircle,
  RefreshCw,
  Save,
  Sparkles,
  Star,
  Trash2,
  Video,
} from 'lucide-react';
import { useToast } from '@vd/ui';
import { api, type RecordingServerSegment, type RecordingServerSession } from '../lib/api';
import { useTabProject, useTabTitle, useTabWorkspaceName, useUnseenWhileHidden } from '../lib/tabTitle';
import { useReleaseLocalRecording } from '../features/recorder/RecorderProvider';
import { confirmDiscardWalkthrough } from '../features/recorder/confirmDiscard';
import { draftEditsForTicket, ticketTypeForSegmentType, type CarriedEdits } from '../features/recorder/filedDraftHandoff';
import { CommittedTicketSet } from '../features/recorder/CommittedTicketSet';
import { DescriptionField } from '../features/ticket/DescriptionField';
import { MarkdownBody } from '../features/comments/MarkdownBody';
import { ReviewAssignees } from '../features/recorder/ReviewAssignees';
import { walkthroughHealth } from '../features/recorder/segmentHealth';
import { reviewProgress } from '../features/recorder/reviewProgress';
import styles from './RecordingReviewPage.module.css';
import { useUnsavedNavigationGuard } from '../app/NavigationGuard';
import { keptFor, useVideoRetentionDays } from '../features/recorder/videoRetention';

type SegmentDraft = {
  title: string;
  description: string;
  /**
   * Null is "let Toado decide" (TOADO-416). Every pin segment arrives as 'bug' because
   * that is the extension's default, and showing it as the selected type told people a
   * decision had been made that nobody made - which is also what filed every
   * walkthrough ticket as a Bug and kept the classifier from ever running.
   */
  segmentType: RecordingServerSegment['segmentType'] | null;
};

function draftTypeLabel(segmentType: SegmentDraft['segmentType']): string {
  if (segmentType === 'bug') return 'Bug report';
  if (segmentType === 'feature_request') return 'Feature request';
  return 'Type chosen when filed';
}

function durationLabel(segment: RecordingServerSegment): string {
  const durationMs = Math.max(0, (segment.endMs ?? segment.startMs) - segment.startMs);
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.floor((durationMs % 60_000) / 1_000);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function hasPendingReviewWork(session: RecordingServerSession | undefined): boolean {
  if (!session) return false;
  if (session.status === 'processing') return true;
  return (session.segments ?? []).some((segment) => !segment.droppedAt && (
    segment.clipUploadStatus === 'pending'
    || segment.clipUploadStatus === 'uploading'
    || (segment.transcriptStatus === 'pending' && session.transcriptionAvailable !== false)
  ));
}

function hasPendingTranscription(session: RecordingServerSession | undefined): boolean {
  return Boolean((session?.segments ?? []).some((segment) => (
    !segment.droppedAt && segment.transcriptStatus === 'pending'
  )));
}

function hasPausedTranscription(session: RecordingServerSession | undefined): boolean {
  return Boolean(session && ['processing', 'ready_for_review'].includes(session.status))
    && session?.transcriptionAvailable === false
    && hasPendingTranscription(session);
}

export function recordingReviewPollingInterval(session: RecordingServerSession | undefined): number | false {
  if (!session || session.status === 'discarded') return false;
  // Committed while a clip is still coming (TOADO-900, Codex review round 7):
  // filed moments' videos land after, and this page shows them from here.
  if (session.status === 'committed') {
    return (session.segments ?? []).some((segment) => !segment.droppedAt
      && (segment.clipUploadStatus === 'pending' || segment.clipUploadStatus === 'uploading')) ? 5_000 : false;
  }
  if (hasPausedTranscription(session)) return 30_000;
  return hasPendingReviewWork(session) ? 2_000 : false;
}

function buildCommittedFallbacks(
  segments: RecordingServerSegment[],
): Record<string, { title: string; thumbnailUrl?: string }> {
  const map: Record<string, { title: string; thumbnailUrl?: string }> = {};
  for (const segment of segments) {
    if (!segment.ticketId) continue;
    const snapshots = activeSnapshots(segment);
    const primary = snapshots.find((snapshot) => snapshot.id === segment.primarySnapshotId)
      ?? snapshots[0];
    map[segment.ticketId] = {
      title: segment.draftTitle ?? '',
      ...(primary?.thumbnailUrl ? { thumbnailUrl: primary.thumbnailUrl } : {}),
    };
  }
  return map;
}

function activeSnapshots(segment: RecordingServerSegment) {
  return segment.snapshots
    .filter((snapshot) => !snapshot.droppedAt)
    .sort((left, right) => left.orderIndex - right.orderIndex);
}

/** Why a clip is gone, in the walkthrough's own workspace's numbers when they are known. */
function expiredClipNote(retentionDays: number | undefined): string {
  return `Walkthrough video is kept ${keptFor(retentionDays, 'for a limited time on each plan')}. The tickets and what was said are kept.`;
}

/**
 * What holds an expired clip's place (TOADO-444): the moment's primary snapshot,
 * or the first kept one with a picture if the primary has none, at the largest size
 * there is. Expiry removes clip bytes and nothing else, so these outlive the video.
 * `number` is its place in the Captured moments rail below, counted from one.
 */
export function expiredClipStill(
  segment: Pick<RecordingServerSegment, 'primarySnapshotId'>,
  snapshots: RecordingServerSegment['snapshots'],
): { url: string; number: number } | undefined {
  const pictured = (snapshot: RecordingServerSegment['snapshots'][number]) => snapshot.screenshotUrl || snapshot.thumbnailUrl;
  const primary = snapshots.findIndex((snapshot) => snapshot.id === segment.primarySnapshotId && pictured(snapshot));
  const at = primary >= 0 ? primary : snapshots.findIndex(pictured);
  if (at < 0) return undefined;
  return { url: pictured(snapshots[at]!)!, number: at + 1 };
}

/**
 * What was said, whole (Jason, 30 Oct): a few lines first, and Show all for a long
 * one, rather than a box that scrolls inside itself and reads as cut off.
 */
function TranscriptText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  const body = useRef<HTMLParagraphElement>(null);
  // Measured folded: whether the text runs past the preview, so short ones get no button.
  useEffect(() => {
    const element = body.current;
    if (!element || open) return;
    setLong(element.scrollHeight > element.clientHeight + 1);
  }, [text, open]);
  return (
    <>
      <p ref={body} className={open ? styles.transcriptOpen : styles.transcriptFolded} data-long={long ? '' : undefined} data-testid="review-transcript-text">{text}</p>
      {(long || open) && (
        <button type="button" className={styles.transcriptMore} aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? 'Show less' : 'Show all'}
        </button>
      )}
    </>
  );
}

type TicketEdits = Partial<{ typeId: string | null; title: string; description: string }>;

function isNotFound(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 404;
}

/**
 * A committed moment's ticket, editable in place (TOADO-417).
 *
 * Edits are held PER FIELD, and that is the design rather than a detail. For the first
 * seconds after a commit the AI is still writing: the rewritten description, the title
 * and the type all arrive over the live channel while somebody may already be typing.
 * A field somebody has touched keeps their text; a field they have not keeps following
 * the ticket. So the AI cannot overwrite what is being typed, and - the subtler half - a
 * stale copy of a field nobody touched cannot be sent over what the AI just wrote.
 * Save sends only the touched fields, and takes back only those.
 */
function CommittedTicketEditor({
  ticketId,
  projectId,
  companyId,
  ticket,
  fetchedAt,
  loadError,
  onDirtyChange,
  onDeleted,
  carried,
}: {
  ticketId: string;
  projectId: string | undefined;
  companyId: string | undefined;
  ticket: Ticket | undefined;
  /** When the ticket was last fetched successfully (the query's `dataUpdatedAt`). */
  fetchedAt: number;
  /** The ticket query's error, so a deleted ticket can be told from a network blip. */
  loadError: unknown;
  onDirtyChange: (dirty: boolean) => void;
  /** The ticket was deleted here, so its card can fold away its clip and pictures (t-ja_zN4). */
  onDeleted?: () => void;
  /**
   * Edits made to this moment's draft before it was filed during the recording
   * (TOADO-900), started as this editor's unsaved edits so the person saves
   * them here. Read once, when the editor first shows.
   */
  carried?: CarriedEdits;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [edits, setEdits] = useState<TicketEdits>(() => ({
    ...(carried?.title !== undefined ? { title: carried.title } : {}),
    ...(carried?.description !== undefined ? { description: carried.description } : {}),
    // A cleared choice is "No type" at once; there is nothing to name.
    ...(carried?.segmentType === null ? { typeId: null } : {}),
  }));
  /** A carried bug or feature choice, until the project's types are there to name it. */
  const [carriedType, setCarriedType] = useState(carried?.segmentType ?? undefined);
  /** Why a carried type could not be named: none in this workspace matches, or the types did not load. */
  const [carriedTypeNote, setCarriedTypeNote] = useState<'unmatched' | 'unloaded' | null>(null);
  const [saving, setSaving] = useState(false);
  /**
   * The ticket is out of reach (review rounds 7 and 8). Learned from a 404 on a re-fetch -
   * the page's live subscription re-fetches on `ticket.deleted` - or from a save that
   * answered 404. The API answers 404 for a deleted ticket AND for one moved to a project
   * this person cannot see, so the page says it may be either, rather than asserting a
   * deletion that may not have happened.
   *
   * Remembered as WHEN, not as a flag, so it recovers: any successful fetch after that
   * moment - the ticket restored, or moved back - brings the editor back. A flag set by
   * a failed save stayed set until a reload, whatever came after. It is the fetch time
   * rather than the ticket object because React Query keeps the same object when a
   * re-fetch returns identical data, so a ticket that came back unchanged would have
   * looked like the one that answered 404.
   */
  const [unreachableSince, setUnreachableSince] = useState<number | null>(null);
  const outOfReach = (unreachableSince !== null && fetchedAt <= unreachableSince) || isNotFound(loadError);
  /**
   * Deleting a moment that was not really a ticket (Jason, 11 Oct): asked once,
   * then gone for good, as a delete from the ticket page is.
   */
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'deleting' | 'deleted'>('idle');
  // The project the ticket is in now (z.ai review round 5), which a move
  // since filing changed; the walkthrough's until the ticket is read.
  const typesProjectId = ticket?.projectId ?? projectId;
  const types = useQuery({
    queryKey: ['ticketTypes', typesProjectId],
    queryFn: () => api.listProjectTicketTypes(typesProjectId!).then((result) => result.items),
    enabled: Boolean(typesProjectId),
  });
  /**
   * A carried choice is named from the workspace's own types, in the
   * workspace's order (Codex review round 6): the rule filing applies
   * (`ticketTypeIdsForSegmentTypes`), where this project's list has its own
   * order and would name a different type. Fetched only while one waits.
   */
  const companyTypes = useQuery({
    queryKey: ['companyTicketTypes', companyId],
    queryFn: () => api.listCompanyTicketTypes(companyId!).then((result) => result.items),
    enabled: Boolean(companyId) && carriedType !== undefined,
  });
  useEffect(() => {
    if (carriedType === undefined) return;
    if (!companyId) {
      setCarriedTypeNote('unmatched');
      setCarriedType(undefined);
      return;
    }
    if (!companyTypes.data) {
      // Said, and still named if the types load later (z.ai review round 5);
      // a type the person picks meanwhile is theirs.
      if (companyTypes.error) setCarriedTypeNote('unloaded');
      return;
    }
    const typeId = ticketTypeForSegmentType(companyTypes.data, carriedType);
    if (typeId) setEdits((current) => ('typeId' in current ? current : { ...current, typeId }));
    setCarriedTypeNote(typeId ? null : 'unmatched');
    setCarriedType(undefined);
  }, [carriedType, companyId, companyTypes.data, companyTypes.error]);
  // A carried type still being named is an unsaved change too (review round 4).
  const hasEdits = Object.keys(edits).length > 0;
  const dirty = !outOfReach && (hasEdits || carriedType !== undefined);
  /** Said until nothing carried is left unsaved (Codex review round 5): after a save the card still passes `carried`. */
  const [carriedUnsaved, setCarriedUnsaved] = useState(Boolean(carried));
  // By what is unsaved, not by reach (z.ai review round 5): a 404 and a
  // recovery bring the edits back, and the note with them.
  const unsaved = hasEdits || carriedType !== undefined;
  useEffect(() => { if (!unsaved) setCarriedUnsaved(false); }, [unsaved]);
  // Through a ref, so the parent's inline callback does not re-run this on every
  // render; only a change in dirtiness is news.
  const notifyDirty = useRef(onDirtyChange);
  notifyDirty.current = onDirtyChange;
  useEffect(() => { notifyDirty.current(dirty); }, [dirty]);
  useEffect(() => () => { notifyDirty.current(false); }, []);

  if (deleteStep === 'deleted') {
    return (
      <div className={styles.committedRecord}>
        <p>You deleted this ticket.</p>
      </div>
    );
  }
  if (outOfReach) {
    return (
      <div className={styles.committedRecord}>
        <p>This ticket is no longer available here. It may have been deleted, or moved to a project you cannot see.</p>
      </div>
    );
  }
  if (loadError && !ticket) {
    return (
      <div className={styles.committedRecord}>
        <p>This ticket could not be loaded. Try again in a moment.</p>
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className={styles.committedRecord}>
        <p><LoaderCircle className={styles.spin} size={14} /> Loading the ticket…</p>
      </div>
    );
  }

  const live = {
    typeId: ticket.type?.id ?? null,
    // A pending AI title is shown as pending, not as the placeholder it replaces.
    title: ticket.titlePending ? '' : ticket.title,
    description: ticket.description,
  };
  const value = {
    typeId: 'typeId' in edits ? edits.typeId ?? null : live.typeId,
    title: 'title' in edits ? edits.title ?? '' : live.title,
    description: 'description' in edits ? edits.description ?? '' : live.description,
  };
  /**
   * Offered types, plus the ticket's own type when the project has since hidden it.
   * Hiding is never retroactive (TOADO-408), so a ticket keeps a hidden type, and a
   * select that did not list it would show the wrong answer - "No type" for a ticket
   * that has one. Display only: the per-field save never sends an untouched type.
   */
  const offered = (types.data ?? []).filter((type) => !type.hidden);
  /** A type this project does not offer, named for which of the two it is (z.ai review round 6). */
  const unofferedName = (id: string, name: string) => !types.data ? name
    : types.data.some((type) => type.id === id) ? `${name} (hidden in this project)` : `${name} (not in this project)`;
  const shown = ticket.type && !offered.some((type) => type.id === ticket.type!.id)
    ? [...offered, { id: ticket.type.id, name: unofferedName(ticket.type.id, ticket.type.name) }]
    : offered;
  // And the one a carried choice named (z.ai review round 4): the server's rule
  // can pick a type this project hides, and a select must show what it saves.
  const carriedPick = 'typeId' in edits && edits.typeId && !shown.some((option) => option.id === edits.typeId)
    ? [...(types.data ?? []), ...(companyTypes.data ?? [])].find((type) => type.id === edits.typeId)
    : undefined;
  const options = carriedPick ? [...shown, { id: carriedPick.id, name: unofferedName(carriedPick.id, carriedPick.name) }] : shown;
  const titleMissing = 'title' in edits && value.title.trim() === '';

  const edit = (change: TicketEdits) => setEdits((current) => ({ ...current, ...change }));
  /**
   * The person has decided the type (z.ai review round 6): by choosing one, by
   * saving one, or by deleting the ticket. A carried choice still waiting to be
   * named is theirs no longer, and its note goes with it.
   */
  const settleCarriedType = () => {
    setCarriedType(undefined);
    setCarriedTypeNote(null);
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || !hasEdits || saving || titleMissing) return;
    setSaving(true);
    const sent = { ...edits };
    try {
      const updated = await api.updateTicket(ticketId, {
        ...('typeId' in sent ? { typeId: sent.typeId ?? null } : {}),
        ...('title' in sent ? { title: value.title.trim() } : {}),
        ...('description' in sent ? { description: value.description } : {}),
      });
      /**
       * Only the fields this save sent are taken from its answer (review round 7).
       *
       * The response is the row as this PATCH left it, which can be OLDER than the cache
       * for a field it did not touch: the AI title can land, and be re-fetched, while the
       * PATCH is in flight. Writing the whole response back made that title vanish from
       * the screen. The re-fetch after it settles on the truth either way.
       */
      queryClient.setQueryData<Ticket>(['ticket', ticketId], (current) => (current ? {
        ...current,
        ...('typeId' in sent ? { type: updated.type } : {}),
        ...('title' in sent ? { title: updated.title, titlePending: updated.titlePending } : {}),
        ...('description' in sent ? { description: updated.description } : {}),
      } : updated));
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      // Only what this save sent (review round 4): a field changed while it was
      // in flight, or a carried type named meanwhile, is still unsaved.
      setEdits((current) => Object.fromEntries(Object.entries(current)
        .filter(([key, kept]) => !(key in sent) || sent[key as keyof TicketEdits] !== kept)) as TicketEdits);
      if ('typeId' in sent) settleCarriedType();
      toast.push({ variant: 'success', title: 'Ticket saved' });
    } catch (error) {
      if (isNotFound(error)) {
        // The edits are kept (z.ai review round 4): a ticket that comes back
        // into reach brings the person's changes back with it.
        setUnreachableSince(Date.now());
        toast.push({
          variant: 'danger',
          title: 'This ticket is no longer available',
          description: 'It may have been deleted or moved, so your changes could not be saved to it.',
        });
      } else {
        toast.push({ variant: 'danger', title: 'Could not save the ticket', description: (error as Error).message });
      }
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setDeleteStep('deleting');
    try {
      await api.deleteTicket(ticketId);
      setEdits({});
      settleCarriedType();
      setDeleteStep('deleted');
      onDeleted?.();
      // The set above and any open board drop it; the live event says so too.
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.push({ variant: 'success', title: 'Ticket deleted' });
    } catch (error) {
      setDeleteStep('confirm');
      toast.push({ variant: 'danger', title: 'Could not delete the ticket', description: (error as Error).message });
    }
  };

  return (
    <form className={styles.editor} onSubmit={(event) => { void save(event); }}>
      <p className={styles.committedNote}>
        {carriedUnsaved
          ? 'This moment was filed as a ticket while you were editing it. Your changes are below, not yet saved: save them to the ticket.'
          : 'This moment is a ticket now. Changes here save straight to it.'}
      </p>
      {carriedType !== undefined && !carriedTypeNote && (
        <p className={styles.committedNote}>Naming the type you chose…</p>
      )}
      {carriedTypeNote && (
        <p className={styles.committedNote}>
          {carriedTypeNote === 'unmatched'
            ? 'The type you chose has no match in this workspace. Choose one below.'
            : 'The types could not be loaded, so the type you chose is not filled in. Choose it below.'}
        </p>
      )}
      {companyId && (
        // Where the ticket is now, not where the walkthrough was recorded: a ticket
        // moved since is assigned from its new project's people.
        <ReviewAssignees
          ticketId={ticketId}
          projectId={ticket.projectId}
          companyId={companyId}
          assignees={ticket.assignees}
        />
      )}
      <label>
        <span>Ticket type</span>
        <select
          value={value.typeId ?? ''}
          disabled={saving}
          onChange={(event) => { settleCarriedType(); edit({ typeId: event.target.value || null }); }}
        >
          <option value="">No type</option>
          {options.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
        </select>
      </label>
      <label>
        <span>Ticket title</span>
        <input
          value={value.title}
          maxLength={500}
          disabled={saving}
          placeholder={ticket.titlePending && !('title' in edits) ? 'Toado is writing a title…' : 'Title'}
          aria-invalid={titleMissing}
          onChange={(event) => edit({ title: event.target.value })}
        />
      </label>
      <label className={styles.descriptionField}>
        <span>Description</span>
        {/* Formatted until clicked, as on the ticket page (Jason, 11 Oct). */}
        <DescriptionField
          className={styles.descriptionBox}
          value={value.description}
          maxLength={100 * 1_024}
          disabled={saving}
          members={[]}
          onChange={(next) => edit({ description: next })}
        />
      </label>
      {deleteStep === 'confirm' || deleteStep === 'deleting' ? (
        <div className={styles.deleteConfirm} role="group" aria-label="Delete this ticket">
          <p>Delete this ticket? It leaves the board for good.</p>
          <div className={styles.editorActions}>
            <button className={styles.secondaryLink} type="button" disabled={deleteStep === 'deleting'} onClick={() => setDeleteStep('idle')}>
              Keep it
            </button>
            <button className={styles.deleteButton} type="button" disabled={deleteStep === 'deleting'} onClick={() => { void remove(); }}>
              {deleteStep === 'deleting' ? <LoaderCircle className={styles.spin} size={15} /> : <Trash2 size={14} />}
              Delete ticket
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.editorActions}>
          <button className={styles.dropButton} type="button" disabled={saving} onClick={() => setDeleteStep('confirm')}>
            <Trash2 size={14} /> Delete
          </button>
          <div className={styles.editorActionsEnd}>
            <Link className={styles.secondaryLink} to={`/x/ticket?t=${encodeURIComponent(ticketId)}`}>
              Open ticket
            </Link>
            <button className={styles.saveButton} type="submit" disabled={!dirty || !hasEdits || saving || titleMissing}>
              {saving ? <LoaderCircle className={styles.spin} size={15} /> : <Save size={15} />}
              Save ticket
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

function SegmentReviewCard({
  recordingId,
  projectId,
  companyId,
  index,
  preloadClip,
  segment,
  transcriptionAvailable,
  readOnly,
  clipRetryable,
  busyKey,
  onSave,
  onDrop,
  onRetryClip,
  onRetryTranscript,
  onUpdateSnapshots,
  onDraftChange,
  onDraftSaved,
  onCommittedDirtyChange,
  retentionDays,
}: {
  recordingId: string;
  projectId: string | undefined;
  /** How long this walkthrough's workspace keeps video (`useVideoRetentionDays`). */
  retentionDays: number | undefined;
  /** The walkthrough's company, whose members can be assigned its tickets. */
  companyId: string | undefined;
  index: number;
  preloadClip: boolean;
  segment: RecordingServerSegment;
  transcriptionAvailable: boolean;
  readOnly: boolean;
  /**
   * Whether this moment's failed clip can be cut again, apart from whether its
   * draft can be edited (Codex review round 5): a moment filed as a ticket is
   * read-only here, and its video is still the walkthrough's to retry.
   */
  clipRetryable: boolean;
  busyKey: string | null;
  onSave: (segment: RecordingServerSegment, draft: SegmentDraft) => Promise<boolean>;
  onDrop: (segment: RecordingServerSegment) => Promise<boolean | void>;
  onRetryClip: () => Promise<boolean | void>;
  onRetryTranscript: (segment: RecordingServerSegment) => Promise<boolean | void>;
  onUpdateSnapshots: (
    segment: RecordingServerSegment,
    input: { orderedSnapshotIds?: string[]; droppedSnapshotIds?: string[]; primarySnapshotId?: string | null },
  ) => Promise<boolean | void>;
  onDraftChange: (segmentId: string, draft: SegmentDraft) => void;
  onDraftSaved: (segmentId: string) => void;
  onCommittedDirtyChange: (segmentId: string, dirty: boolean) => void;
}) {
  /**
   * The ticket this moment became, read LIVE (TOADO-417).
   *
   * The committed card used to show the segment's draft: a snapshot of the raw
   * transcript taken at the moment of commit. The ticket's real description is the AI
   * rewrite, which lands a few seconds later and which this page never showed - so it
   * looked as though the rewrite had not happened. Same key the committed set above
   * uses, so both read one cache, and its live subscription refreshes this card too
   * when the rewrite, the AI title or the classifier's type arrives.
   */
  const committedTicketId = readOnly ? segment.ticketId : undefined;
  const ticketQuery = useQuery({
    queryKey: ['ticket', committedTicketId],
    queryFn: () => api.getTicket(committedTicketId!),
    enabled: Boolean(committedTicketId),
    staleTime: 10_000,
  });
  const liveTicket = ticketQuery.data;
  const serverDraft = useMemo<SegmentDraft>(() => ({
    title: segment.draftTitle ?? '',
    description: segment.draftDescription ?? '',
    segmentType: segment.segmentTypeChosen ? segment.segmentType : null,
  }), [segment.draftDescription, segment.draftTitle, segment.segmentType, segment.segmentTypeChosen]);
  const [draft, setDraft] = useState(serverDraft);
  const [dirty, setDirty] = useState(false);
  /** Deleted from this card's editor: the card folds to its heading (t-ja_zN4). */
  const [deleted, setDeleted] = useState(false);

  /**
   * The draft as it was when editing began (review round 4). The server's copy
   * moves on while the person types (a transcript lands in the description), so
   * comparing with it would read a field they never touched as an edit, and
   * carry an empty one over the ticket's write-up.
   */
  const [editBase, setEditBase] = useState(serverDraft);
  useEffect(() => {
    if (!dirty) {
      setDraft(serverDraft);
      setEditBase(serverDraft);
    }
  }, [dirty, serverDraft]);
  // Filed while being edited (TOADO-900): what was changed becomes the ticket
  // editor's unsaved edits, which it reads when it first shows.
  const carriedEdits = committedTicketId && dirty ? draftEditsForTicket(draft, editBase) : null;

  const snapshots = activeSnapshots(segment);
  /**
   * Pruned, or being pruned. `expiring` is a clip whose removal has been
   * committed but whose object may still exist for a moment; the asset route
   * already refuses it, so to this page it is gone (TOADO-367).
   */
  const gone = segment.clipUploadStatus === 'expired' || segment.clipUploadStatus === 'expiring';
  const still = gone ? expiredClipStill(segment, snapshots) : undefined;
  const duplicateRisk = segment.transcriptLastErrorCode === 'provider_outcome_unknown';
  /**
   * Skipped because the workspace used its transcription minutes for the month
   * (TOADO-496). Not a failure, so it does not say one, and there is nothing to
   * retry until the minutes reset.
   */
  const overAllowance = segment.transcriptStatus === 'failed' && segment.transcriptLastErrorCode === 'plan_allowance';
  const terminalPendingTranscript = readOnly && segment.transcriptStatus === 'pending';
  const saveKey = `save:${segment.id}`;
  const disabled = readOnly || busyKey !== null;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await onSave(segment, draft)) {
      setDirty(false);
      onDraftSaved(segment.id);
    }
  };
  const changeDraft = (change: Partial<SegmentDraft>) => {
    setDraft((current) => {
      const next = { ...current, ...change };
      onDraftChange(segment.id, next);
      return next;
    });
    setDirty(true);
  };
  const reorder = async (from: number, to: number) => {
    const ordered = snapshots.map((snapshot) => snapshot.id);
    [ordered[from], ordered[to]] = [ordered[to]!, ordered[from]!];
    await onUpdateSnapshots(segment, { orderedSnapshotIds: ordered });
  };

  /**
   * A deleted ticket leaves the whole card, not only its editor (Jason, t-ja_zN4).
   *
   * Its clip, transcript and captured moments stayed on the page beside "You
   * deleted this ticket.", which read as though only half of it had gone. The
   * heading stays, so the walkthrough's numbering still adds up.
   */
  if (deleted) {
    return (
      <article className={`${styles.segmentCard} ${styles.segmentDeleted}`} aria-labelledby={`segment-${segment.id}-title`}>
        <div className={styles.segmentHeader}>
          <div className={styles.segmentNumber}>{String(index + 1).padStart(2, '0')}</div>
          <div>
            <span>Deleted</span>
            <h2 id={`segment-${segment.id}-title`}>{liveTicket?.title || segment.draftTitle || `Walkthrough moment ${index + 1}`}</h2>
          </div>
          <p className={styles.deletedNote}>You deleted this ticket.</p>
        </div>
      </article>
    );
  }

  return (
    <article className={styles.segmentCard} aria-labelledby={`segment-${segment.id}-title`}>
      <div className={styles.segmentHeader}>
        <div className={styles.segmentNumber}>{String(index + 1).padStart(2, '0')}</div>
        <div>
          {/* Committed, the heading is the ticket's: its type and its (possibly AI)
              title. Before that it is the draft's, and an unchosen type says so
              rather than wearing the recorder's default. */}
          <span>{liveTicket
            ? liveTicket.type?.name ?? 'No type'
            : draftTypeLabel(draft.segmentType)}</span>
          <h2 id={`segment-${segment.id}-title`}>
            {liveTicket
              ? liveTicket.titlePending ? 'Writing a title…' : liveTicket.title
              : segment.draftTitle || `Walkthrough moment ${index + 1}`}
          </h2>
        </div>
        <div className={styles.duration}><Clock3 size={14} /> {durationLabel(segment)}</div>
      </div>

      <div className={styles.segmentBody}>
        <div className={styles.mediaColumn}>
          <div className={styles.videoFrame} data-status={segment.clipUploadStatus ?? 'pending'}>
            {segment.clipUrl && segment.clipUploadStatus === 'ready' ? (
              <video controls preload={preloadClip ? 'metadata' : 'none'} src={segment.clipUrl}
                poster={snapshots[0]?.thumbnailUrl}>
                Your browser cannot play this walkthrough clip.
              </video>
            ) : still ? (
              // The note sits under the picture, not over it, so none of it is hidden.
              <figure className={styles.expiredStill}>
                <img src={still.url} alt={`Captured moment ${still.number}, in place of the expired clip`} />
                <figcaption>
                  <Video size={15} aria-hidden="true" />
                  <span><strong>This clip has expired</strong> {expiredClipNote(retentionDays)}</span>
                </figcaption>
              </figure>
            ) : (
              <div className={styles.mediaPlaceholder}>
                {/*
                  * Three states, not two. A pruned clip (TOADO-367) is never
                  * coming back, so the spinner and "while this finishes" were
                  * a promise this page could not keep - and a committed
                  * session stops polling, so it spun until the tab closed.
                  *
                  * `expiring` counts as gone because it already is: the claim
                  * commits before the object is deleted and the asset route
                  * refuses it from that moment.
                  */}
                {segment.clipUploadStatus === 'failed' ? <CircleAlert />
                  : gone ? <Video />
                  : <LoaderCircle className={styles.spin} />}
                <strong>
                  {segment.clipUploadStatus === 'failed' ? 'Clip processing failed'
                    : gone ? 'This clip has expired'
                    : 'Preparing this clip'}
                </strong>
                <span>
                  {gone ? expiredClipNote(retentionDays) : 'Your recording remains saved while this finishes.'}
                </span>
                {segment.clipUploadStatus === 'failed' && clipRetryable && (
                  <button type="button" disabled={busyKey !== null} onClick={() => { void onRetryClip(); }}>
                    <RefreshCw size={14} /> Retry clips
                  </button>
                )}
              </div>
            )}
          </div>

          <div className={styles.transcript} data-status={segment.transcriptStatus ?? 'pending'}>
            <div>
              <Sparkles size={15} />
              <strong>Voice transcript</strong>
              <span>{segment.transcriptStatus === 'ready'
                ? 'Ready'
                : overAllowance
                  ? 'Not included'
                : segment.transcriptStatus === 'failed'
                  ? 'Needs attention'
                  : segment.transcriptStatus === 'not_requested'
                    ? 'Not requested'
                    : terminalPendingTranscript
                      ? 'Stopped'
                    : !transcriptionAvailable
                      ? 'Paused'
                    : 'Processing'}</span>
            </div>
            {segment.transcriptText ? <TranscriptText text={segment.transcriptText} /> : (
              <p>{duplicateRisk
                ? 'The prior provider outcome could not be confirmed. Retry is disabled to prevent sending this audio twice.'
                : terminalPendingTranscript
                  ? 'Transcription ended when this walkthrough closed. No transcript will be attached.'
                  : overAllowance
                    ? 'Transcript not included: this workspace has used its narration minutes for the month. They reset on the 1st. You can still write the ticket yourself.'
                  : segment.transcriptStatus === 'failed'
                ? 'The transcript could not be created. You can still write the ticket yourself.'
                : segment.transcriptStatus === 'not_requested'
                  ? 'No audio was sent for transcription. You can write the ticket yourself or request it when enabled.'
                  : !transcriptionAvailable
                    ? 'New transcription dispatch is paused. Existing provider attempts may still complete.'
                  : 'Whisper is turning your narration into searchable ticket context.'}</p>
            )}
            {(segment.transcriptStatus === 'failed' || segment.transcriptStatus === 'not_requested')
              && !duplicateRisk && !overAllowance && !readOnly && (
              <button type="button" disabled={busyKey !== null || !transcriptionAvailable} onClick={() => { void onRetryTranscript(segment); }}>
                <RefreshCw size={13} /> {segment.transcriptStatus === 'failed' ? 'Retry transcript' : 'Request transcript'}
              </button>
            )}
          </div>
        </div>

        {readOnly && committedTicketId ? (
          /**
           * Once a walkthrough has committed, the TICKET is the editable thing, and
           * it is edited here (TOADO-417). Jason asked for type, title and
           * description in this area, and the alternative - a link to the ticket
           * page for every correction - is five round trips for a five-ticket
           * walkthrough on exactly the screen built for reviewing them together.
           */
          <CommittedTicketEditor
            ticketId={committedTicketId}
            projectId={projectId}
            companyId={companyId}
            ticket={liveTicket}
            fetchedAt={ticketQuery.dataUpdatedAt}
            loadError={ticketQuery.error}
            onDirtyChange={(dirty) => onCommittedDirtyChange(segment.id, dirty)}
            onDeleted={() => setDeleted(true)}
            {...(carriedEdits ? { carried: carriedEdits } : {})}
          />
        ) : readOnly ? (
          <div className={styles.committedRecord}>
            <p>This moment did not become a ticket, and this is what it was when the walkthrough finished.</p>
            <dl>
              <dt>Title</dt>
              <dd>{draft.title || <em>No title</em>}</dd>
              <dt>Description</dt>
              <dd className={styles.committedDescription}>
                {draft.description ? <MarkdownBody body={draft.description} members={[]} /> : <em>No description</em>}
              </dd>
            </dl>
          </div>
        ) : (
        <form className={styles.editor} onSubmit={(event) => { void submit(event); }}>
          <label>
            <span>Ticket type</span>
            <select
              value={draft.segmentType ?? ''}
              disabled={disabled}
              onChange={(event) => {
                const value = event.target.value;
                changeDraft({
                  segmentType: value === 'bug' || value === 'feature_request' ? value : null,
                });
              }}
            >
              <option value="">Let Toado decide</option>
              <option value="bug">Bug</option>
              <option value="feature_request">Feature request</option>
            </select>
          </label>
          <label>
            <span>Ticket title</span>
            <input
              value={draft.title}
              maxLength={500}
              disabled={disabled}
              placeholder={draft.segmentType === 'bug'
                ? 'What went wrong?'
                : draft.segmentType === 'feature_request' ? 'What should be possible?' : 'What is this moment about?'}
              onChange={(event) => {
                changeDraft({ title: event.target.value });
              }}
            />
          </label>
          <label className={styles.descriptionField}>
            <span>Description</span>
            <DescriptionField
              className={styles.descriptionBox}
              value={draft.description}
              maxLength={100 * 1_024}
              disabled={disabled}
              members={[]}
              placeholder="Add the details a teammate needs to act on this ticket."
              onChange={(next) => {
                changeDraft({ description: next });
              }}
            />
          </label>
          {!readOnly && (
            <div className={styles.editorActions}>
              <button className={styles.dropButton} type="button" disabled={busyKey !== null} onClick={() => { void onDrop(segment); }}>
                <Trash2 size={14} /> Drop moment
              </button>
              <button className={styles.saveButton} type="submit" disabled={!dirty || busyKey !== null}>
                {busyKey === saveKey ? <LoaderCircle className={styles.spin} size={15} /> : <Save size={15} />}
                Save ticket
              </button>
            </div>
          )}
        </form>
        )}
      </div>

      <section className={styles.snapshotSection} aria-label={`Snapshots for ticket ${index + 1}`}>
        <div className={styles.snapshotHeading}>
          <div><ImageIcon size={16} /><strong>Captured moments</strong></div>
          <span>{snapshots.length} image{snapshots.length === 1 ? '' : 's'}</span>
        </div>
        {snapshots.length === 0 ? (
          <div className={styles.emptySnapshots}>No screenshots were retained for this moment.</div>
        ) : (
          <div className={styles.snapshotRail}>
            {snapshots.map((snapshot, snapshotIndex) => (
              <article className={styles.snapshotCard} key={snapshot.id} data-primary={segment.primarySnapshotId === snapshot.id ? 'true' : 'false'}>
                {snapshot.thumbnailUrl || snapshot.screenshotUrl ? (
                  <img src={snapshot.thumbnailUrl || snapshot.screenshotUrl} alt={`Captured moment ${snapshotIndex + 1}`} />
                ) : (
                  <div className={styles.snapshotPending}><LoaderCircle className={styles.spin} /><span>Uploading</span></div>
                )}
                <div className={styles.snapshotMeta}>
                  <span>{snapshot.trigger === 'manual' ? 'Captured' : snapshot.trigger === 'start' ? 'Start' : 'Finish'}</span>
                  {segment.primarySnapshotId === snapshot.id && <strong><Star size={11} /> Primary</strong>}
                </div>
                {!readOnly && (
                  <div className={styles.snapshotActions}>
                    <button type="button" aria-label={`Move snapshot ${snapshotIndex + 1} left`} disabled={snapshotIndex === 0 || busyKey !== null} onClick={() => { void reorder(snapshotIndex, snapshotIndex - 1); }}><ArrowLeft size={13} /></button>
                    <button type="button" aria-label={`Move snapshot ${snapshotIndex + 1} right`} disabled={snapshotIndex === snapshots.length - 1 || busyKey !== null} onClick={() => { void reorder(snapshotIndex, snapshotIndex + 1); }}><ArrowRight size={13} /></button>
                    <button
                      type="button"
                      aria-label={`Set snapshot ${snapshotIndex + 1} as primary`}
                      disabled={snapshot.uploadStatus !== 'ready' || segment.primarySnapshotId === snapshot.id || busyKey !== null}
                      onClick={() => { void onUpdateSnapshots(segment, { primarySnapshotId: snapshot.id }); }}
                    ><Star size={13} /></button>
                    <button
                      type="button"
                      aria-label={`Drop snapshot ${snapshotIndex + 1}`}
                      disabled={busyKey !== null}
                      onClick={() => { void onUpdateSnapshots(segment, { droppedSnapshotIds: [snapshot.id] }); }}
                    ><Trash2 size={13} /></button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
      <span className={styles.recordingId}>Recording {recordingId.slice(0, 12)}</span>
    </article>
  );
}

export function RecordingReviewPage() {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const releaseLocalRecording = useReleaseLocalRecording();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const running = useRef(false);
  const [createdTicketIds, setCreatedTicketIds] = useState<string[]>([]);
  const [dirtyDrafts, setDirtyDrafts] = useState<Record<string, SegmentDraft>>({});
  // Committed tickets with unsaved edits, so leaving the page asks first for these
  // too (TOADO-417). Returns the same object when nothing changed, which is what
  // keeps a card reporting its state from re-rendering the page.
  const [dirtyCommitted, setDirtyCommitted] = useState<Record<string, true>>({});
  const setCommittedDirty = useCallback((segmentId: string, dirty: boolean) => {
    setDirtyCommitted((current) => {
      if (Boolean(current[segmentId]) === dirty) return current;
      const next = { ...current };
      if (dirty) next[segmentId] = true;
      else delete next[segmentId];
      return next;
    });
  }, []);

  const recordingQuery = useQuery({
    queryKey: ['recording-review', recordingId],
    queryFn: () => api.getRecording(recordingId!),
    enabled: Boolean(recordingId),
    refetchInterval: (query) => recordingReviewPollingInterval(query.state.data),
  });
  const session = recordingQuery.data;
  // This tab's title: where the walkthrough is, with a dot when it became
  // ready while the tab was in the background (TOADO-905).
  const tabProject = useTabProject(session?.projectId);
  const tabWorkspace = useTabWorkspaceName();
  const { unseen: readyUnseen, notice: noticeReady } = useUnseenWhileHidden();
  const reviewStatus = session?.status;
  useEffect(() => { if (reviewStatus === 'ready_for_review') noticeReady(); }, [reviewStatus, noticeReady]);
  useTabTitle({
    parts: [
      reviewStatus === 'processing' ? 'Processing walkthrough…' : reviewStatus === 'ready_for_review' ? 'Ready to review' : null,
      tabProject?.name, tabWorkspace,
    ],
    project: tabProject,
    unseen: readyUnseen && reviewStatus === 'ready_for_review',
  });
  const retentionDays = useVideoRetentionDays(session?.companyId);
  const segments = useMemo(
    () => (session?.segments ?? []).filter((segment) => !segment.droppedAt),
    [session?.segments],
  );
  const firstPlayableClipId = segments.find(segment => segment.clipUploadStatus === 'ready' && segment.clipUrl)?.id;
  /**
   * Moments filed as tickets during the recording (TOADO-900). Each is a ticket
   * already, edited as one on its card; the commit files only the rest.
   */
  const filedCount = segments.filter((segment) => segment.ticketId).length;
  const unfiledCount = segments.length - filedCount;
  const readOnly = session?.status === 'committed' || session?.status === 'discarded';
  // As the server decides it (Codex review round 14); an older server says
  // nothing, and then a committed walkthrough only while its master is kept.
  const clipRetryable = session?.clipsRetryable ?? (session?.status !== 'discarded'
    && (session?.status !== 'committed' || session?.masterUploadStatus === 'ready'));
  const hasUnsavedDrafts = Object.keys(dirtyDrafts).length > 0;
  useUnsavedNavigationGuard(
    hasUnsavedDrafts || Object.keys(dirtyCommitted).length > 0,
    'Leave without saving these ticket changes?',
  );
  /**
   * A moment filed while its draft was being edited (TOADO-900): the draft now
   * lives in that card's ticket editor as unsaved edits, which guards leaving
   * on its own, so it is not also held here.
   */
  useEffect(() => {
    if (!segments.some((segment) => segment.ticketId && dirtyDrafts[segment.id])) return;
    setDirtyDrafts((current) => Object.fromEntries(Object.entries(current)
      .filter(([segmentId]) => !segments.find((segment) => segment.id === segmentId)?.ticketId)));
  }, [segments, dirtyDrafts]);
  /**
   * Nor one whose moment is gone, dropped elsewhere (z.ai review round 8 of the
   * page): it has no card to save it from, and the commit would be sent it.
   */
  useEffect(() => {
    if (!session) return;
    if (Object.keys(dirtyDrafts).every((segmentId) => segments.some((segment) => segment.id === segmentId))) return;
    setDirtyDrafts((current) => Object.fromEntries(Object.entries(current)
      .filter(([segmentId]) => segments.some((segment) => segment.id === segmentId))));
  }, [segments, dirtyDrafts]);

  const run = async (key: string, operation: () => Promise<void>): Promise<boolean> => {
    // A ref as well as the state (z.ai review round 5): two presses in one
    // tick both see the state before it changes.
    if (busyKey || running.current) return false;
    running.current = true;
    setBusyKey(key);
    try {
      await operation();
      return true;
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not update walkthrough', description: (error as Error).message });
      return false;
    } finally {
      running.current = false;
      setBusyKey(null);
    }
  };
  /**
   * A re-read after something was done. It does not throw (z.ai review round
   * 7): an action that succeeded is not reported as failed because the read
   * after it did; the page says it is showing older state instead.
   * `refreshStrict` throws, for the one caller that branches on it.
   */
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['recording-review', recordingId] });
  };
  const refreshStrict = async () => {
    await queryClient.invalidateQueries({ queryKey: ['recording-review', recordingId] }, { throwOnError: true });
  };

  if (recordingQuery.isLoading) {
    return <main className={styles.page}><div className={styles.loading}><LoaderCircle className={styles.spin} /><strong>Loading your walkthrough</strong></div></main>;
  }
  // Only with nothing to show (Codex review round 7): a read that failed after
  // the page had the walkthrough keeps it, and the edits in its editors.
  if (!session || !recordingId) {
    return (
      <main className={styles.page}>
        <div className={styles.errorState}><CircleAlert /><h1>Walkthrough unavailable</h1><p>{recordingQuery.error instanceof Error ? recordingQuery.error.message : 'This recording could not be loaded.'}</p><Link to="/recordings">Back to recorder</Link></div>
      </main>
    );
  }

  const saveSegment = async (segment: RecordingServerSegment, draft: SegmentDraft) => run(`save:${segment.id}`, async () => {
    await api.updateRecordingReviewSegment(recordingId, segment.id, {
      draftTitle: draft.title,
      draftDescription: draft.description,
      segmentType: draft.segmentType,
      // Every non-null type on this page is somebody's pick: an unchosen one is shown,
      // and sent, as null. Said explicitly so the server need not infer it.
      ...(draft.segmentType !== null ? { segmentTypeChosen: true } : {}),
    });
    await refresh();
    toast.push({ variant: 'success', title: 'Ticket draft saved', description: 'Your walkthrough changes are ready to commit.' });
  });
  const dropSegment = async (segment: RecordingServerSegment) => {
    if (!window.confirm('Drop this walkthrough moment? It will not create a ticket.')) return;
    await run(`drop:${segment.id}`, async () => {
      await api.dropRecordingReviewSegment(recordingId, segment.id);
      setDirtyDrafts((current) => {
        const next = { ...current };
        delete next[segment.id];
        return next;
      });
      await refresh();
    });
  };
  const updateSnapshots = async (
    segment: RecordingServerSegment,
    input: { orderedSnapshotIds?: string[]; droppedSnapshotIds?: string[]; primarySnapshotId?: string | null },
  ) => run(`snapshots:${segment.id}`, async () => {
    await api.updateRecordingReviewSnapshots(recordingId, segment.id, input);
    await refresh();
  });

  // The walkthrough's tickets, wherever they were filed: during the recording
  // or by the commit, whose answer names only the ones it made.
  const committedTicketIds = [...new Set([
    ...segments.flatMap((segment) => segment.ticketId ? [segment.ticketId] : []),
    ...createdTicketIds,
  ])];

  // Draft title and primary snapshot from each segment, so the finished set
  // renders immediately rather than as empty cards while the real tickets
  // load — and still says something useful if a fetch fails outright.
  //
  // Deliberately NOT a useMemo: this sits below the loading/error early
  // returns, so a hook here changes the hook count between renders. The work
  // is a walk over a handful of segments and only matters once committed.
  const committedFallbacks = buildCommittedFallbacks(segments);
  const health = walkthroughHealth(segments, session.transcriptionAvailable !== false);
  const progress = reviewProgress(session);

  return (
    <main className={styles.page}>
      {/* Kept on screen through a failed re-read, and said (z.ai review round 7). */}
      {recordingQuery.error && (
        <p className={styles.committedNote} role="status">
          This walkthrough could not be refreshed, so what is shown may be out of date.
        </p>
      )}
      <header className={styles.header}>
        {/* To the list of finished walkthroughs (TOADO-439), on its own tab. */}
        <Link className={styles.backLink} to="/recordings/history"><ArrowLeft size={15} /> All walkthroughs</Link>
        <div className={styles.headingRow}>
          <div>
            <h1>Turn the walkthrough into tickets</h1>
            <p>Review each narrated moment, keep the strongest snapshots, then create the full ticket set in one commit.</p>
          </div>
          {/*
            One arc, derived once (TOADO-304). The states it can be in are
            enumerated in `reviewProgress`, tested there against every
            combination, and rendered here without a second opinion about any
            of them. What this replaced was the same information spread over a
            spinner per clip and a line per transcript, which between them
            never answered "how long until I can act on this".
          */}
          <div className={styles.statusCard} data-status={progress.stage}>
            {/*
              Exactly one icon. A ready walkthrough with no transcripts left
              to arrive is not moving and is also not a problem, and an
              earlier version drew it a tick and an alert at the same time.
            */}
            {progress.stage === 'failed' ? <CircleAlert size={18} />
              : progress.stage === 'ready' || progress.stage === 'committed' || progress.stage === 'discarded'
                ? <Check size={18} />
                : progress.moving
                  ? <LoaderCircle className={styles.spin} size={18} />
                  : <CircleAlert size={18} />}
            <div>
              <strong>{progress.headline}</strong>
              <span>
                {progress.detail
                  ?? `${segments.length} ticket${segments.length === 1 ? '' : 's'} in this walkthrough`}
              </span>
              {progress.uploadFraction !== undefined && (
                <progress
                  className={styles.uploadBar}
                  value={progress.uploadFraction}
                  max={1}
                  aria-label={`Uploading the recording, ${Math.round(progress.uploadFraction * 100)} percent`}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      {session.status !== 'committed' && health.totalLoss && (
        <div className={styles.processingBanner} role="alert" data-tone="failed">
          <CircleAlert />
          <div>
            <strong>No clip in this walkthrough could be processed</strong>
            <span>
              Waiting will not change this. The screenshots and your notes are still here, so you
              can create the tickets without video, or discard the walkthrough and record again.
            </span>
          </div>
        </div>
      )}

      {session.status !== 'committed' && !health.totalLoss && health.failed > 0 && (
        <div className={styles.processingBanner} role="status" data-tone="failed">
          <CircleAlert />
          <div>
            <strong>
              {health.failed} moment{health.failed === 1 ? '' : 's'} had a problem
            </strong>
            <span>
              {/* Not "the other N": a failed moment is still committable, which
                  is the whole point of this banner. Subtracting the failures
                  from the committable count produced "the other 0 can still be
                  created" whenever every moment had some problem — two
                  sentences contradicting each other on the screen whose job is
                  honesty about partial failure. */}
              Marked below. All {health.committable} can still be filed: a moment with a
              missing clip keeps its screenshot and notes.
            </span>
          </div>
        </div>
      )}

      {hasPendingReviewWork(session) && !health.totalLoss && (
        <div className={styles.processingBanner} role="status">
          <LoaderCircle className={styles.spin} />
          {hasPausedTranscription(session) ? (
            <div><strong>Video clips are still finishing</strong><span>New transcription dispatch is paused. Existing provider attempts may still complete, and this page checks availability periodically.</span></div>
          ) : (
            <div><strong>You can start editing now</strong><span>Video clips and Whisper transcripts are still finishing. This page refreshes automatically.</span></div>
          )}
        </div>
      )}

      {session.status === 'ready_for_review' && hasPausedTranscription(session) && (
        <div className={styles.processingBanner} role="status">
          <CircleAlert />
          <div><strong>Voice transcription is paused</strong><span>New transcription dispatch is paused. Existing provider attempts may still complete. You can finish the review now or leave this page open for an availability check.</span></div>
        </div>
      )}

      {session.status === 'committed' && (
        <CommittedTicketSet
          ticketIds={committedTicketIds}
          fallbackByTicketId={committedFallbacks}
          {...(session.projectId ? { projectId: session.projectId } : {})}
        />
      )}

      <section className={styles.segmentList} aria-label="Walkthrough ticket drafts">
        {segments.length === 0 ? (
          <div className={styles.emptyState}><PlayCircle /><h2>No ticket moments remain</h2><p>Return to the recorder and create a walkthrough with at least one marked ticket.</p></div>
        ) : segments.map((segment, index) => (
          <SegmentReviewCard
            key={segment.id}
            recordingId={recordingId}
            projectId={session.projectId}
            retentionDays={retentionDays}
            companyId={session.companyId}
            index={index}
            preloadClip={segment.id === firstPlayableClipId}
            segment={segment}
            transcriptionAvailable={session.transcriptionAvailable !== false}
            readOnly={readOnly || Boolean(segment.ticketId)}
            clipRetryable={clipRetryable}
            busyKey={busyKey}
            onSave={saveSegment}
            onDrop={dropSegment}
            onRetryClip={() => run('retry:clips', async () => { await api.retryRecordingClips(recordingId); await refresh(); })}
            onRetryTranscript={(candidate) => run(`retry:transcript:${candidate.id}`, async () => { await api.retryRecordingTranscription(recordingId, candidate.id); await refresh(); })}
            onUpdateSnapshots={updateSnapshots}
            onDraftChange={(segmentId, draft) => setDirtyDrafts((current) => ({ ...current, [segmentId]: draft }))}
            onDraftSaved={(segmentId) => setDirtyDrafts((current) => {
              const next = { ...current };
              delete next[segmentId];
              return next;
            })}
            onCommittedDirtyChange={setCommittedDirty}
          />
        ))}
      </section>

      {!readOnly && (
        <footer className={styles.commitBar}>
          <div>
            <strong>{unfiledCount} ticket{unfiledCount === 1 ? '' : 's'} ready to create</strong>
            {filedCount > 0 && (
              <span>{filedCount} already filed during the recording.</span>
            )}
            <span>{session.status === 'ready_for_review'
              ? hasUnsavedDrafts
                ? 'Your on-screen edits will be saved atomically with the full ticket set.'
                : hasPausedTranscription(session)
                  ? 'Voice transcription is paused. You can still create the full ticket set.'
                  : hasPendingTranscription(session)
                    ? 'Voice transcription is still finishing. You can create the ticket set now or wait for the transcript.'
                  : 'Everything is processed. One click creates the full set.'
              : 'Commit unlocks when clip processing finishes.'}</span>
          </div>
          <div className={styles.commitActions}>
            <button
              className={styles.discardButton}
              type="button"
              disabled={busyKey !== null}
              onClick={() => {
                if (!confirmDiscardWalkthrough(filedCount)) return;
                void run('discard', async () => {
                  await api.discardRecording(recordingId);
                  // As Commit does: nothing of it stays here to be offered back.
                  await releaseLocalRecording(recordingId).catch(() => {});
                  navigate('/recordings');
                });
              }}
            ><Trash2 size={15} /> Discard</button>
            <button
              className={styles.commitButton}
              type="button"
              disabled={session.status !== 'ready_for_review' || segments.length === 0 || busyKey !== null}
              onClick={() => {
                void run('commit', async () => {
                  // Drafts for moments still to file: one filed meanwhile is its ticket now.
                  const filedIds = new Set(segments.filter((segment) => segment.ticketId).map((segment) => segment.id));
                  const result = await api.commitRecording(recordingId, Object.entries(dirtyDrafts).filter(([segmentId]) => !filedIds.has(segmentId)).map(([segmentId, draft]) => ({
                    segmentId,
                    draftTitle: draft.title,
                    draftDescription: draft.description,
                    segmentType: draft.segmentType,
                    ...(draft.segmentType !== null ? { segmentTypeChosen: true } : {}),
                  }))).catch(async (error: unknown) => {
                    // Filed after this page last looked: read it again, which carries
                    // any edits to that moment to its ticket, and ask for another press.
                    if ((error as { code?: string }).code !== 'recording_draft_conflict') throw error;
                    const refreshed = await refreshStrict().then(() => true, () => false);
                    throw new Error(refreshed
                      ? 'A moment was filed as a ticket while you were committing. Your edits to it are on its ticket below, not yet saved: save them there. Press Create again for the rest.'
                      : 'A moment was filed as a ticket while you were committing, and this page could not be updated. Your edits are still here: press Create again in a moment.');
                  });
                  setCreatedTicketIds(result.tickets.map((ticket) => ticket.id));
                  setDirtyDrafts({});
                  // The tickets are filed and the server holds the media, so
                  // this browser's chunks and upload record are dead weight.
                  // Left behind, they outlive the session's active window and
                  // the recorder page lists the committed walkthrough back as
                  // unfinished work. Best-effort: a failed local sweep must
                  // never make a successful commit look like it failed.
                  await releaseLocalRecording(recordingId).catch(() => {});
                  // Done whether or not the view refreshes (z.ai review round 4):
                  // a failed read must not invite a second Create.
                  await refresh().catch(() => undefined);
                  const created = result.tickets.length;
                  toast.push(created === 0
                    // Everything was filed during the recording; this closed it.
                    ? { variant: 'success', title: 'Walkthrough finished', description: 'Its tickets are already on the board.' }
                    : {
                      variant: 'success',
                      title: created === 1 ? 'Ticket created' : 'Tickets created',
                      description: created === 1 ? 'The walkthrough ticket is now on the board.' : `${created} walkthrough tickets are now on the board.`,
                    });
                });
              }}
            >{busyKey === 'commit' ? <LoaderCircle className={styles.spin} size={16} /> : <FileCheck2 size={16} />} {unfiledCount === 0 ? 'Finish walkthrough' : unfiledCount === 1 ? 'Create 1 ticket' : `Create ${unfiledCount} tickets`}</button>
          </div>
        </footer>
      )}
    </main>
  );
}
